import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";
import { UserTypeEnum } from "@/api/dmp_common/interface";
import { Button, Dropdown, Tag } from "@ad/anq-ui";
import {
  FileImage,
  NormalUser,
  DirectionDown,
  NormalMail,
  PromptQuestionCircle,
} from "src/Icons";
import { useRouter } from "next/router";
import DrawerMessage from "./drawer_message";
import {
  HELP_CENTER_URL_MAP,
  NavigationItem,
  navigationListWithMenuList,
} from "./utils";
import GlobalContext from "models/globalContext";
import { logout } from "apis/app";
import Link from "next/link";
import classnames from "classnames";
import { hasAuth } from "@/common/hooks/use_auth";
import classNames from "classnames";

import styles from "./index.module.scss";

const userTypeTagImageMap = {
  [UserTypeEnum.INIT]:
    "https://commimg.pddpic.com/upload/dmp/dmp_tag_0.png.slim.png",
  [UserTypeEnum.BRAND]:
    "https://commimg.pddpic.com/upload/dmp/dmp_tag_10.png.slim.png",
  [UserTypeEnum.SUPER]:
    "https://commimg.pddpic.com/upload/dmp/dmp_tag_20.png.slim.png",
  [UserTypeEnum.NORMAL]:
    "https://commimg.pddpic.com/upload/dmp/dmp_tag_30_2.png.slim.png",
  [UserTypeEnum.ADVANCED]:
    "https://commimg.pddpic.com/upload/dmp/dmp_tag_40.png.slim.png",
  [UserTypeEnum.MIDDLE]:
    "https://commimg.pddpic.com/upload/dmp/dmp_tag_50_2.png.slim.png",
};

const NavHeader = () => {
  const {
    name = "",
    userPicUrl,
    mallId = "",
    userType,
    resourceAuthList,
    mallOwner,
    username = "",
  } = useContext(GlobalContext);
  const router = useRouter();
  const { pathname } = router;
  const [drawerMessageOpen, setDrawerMessageOpen] = useState(false);
  const navigationTimer = useRef(null);

  // 过滤掉没有权限的导航:
  // 导航对应的menuList种只要有一个有权限则展示对应的导航; 导航如果没设置resourceCode, 不需要判断权限直接展示
  const navigationListWithPermission = useMemo(() => {
    return navigationListWithMenuList?.filter(
      (item) =>
        item.resourceCode === undefined ||
        hasAuth(resourceAuthList, item.resourceCode, true) ||
        item.menuList.some((menuItem) =>
          hasAuth(resourceAuthList, menuItem.name, true)
        )
    );
  }, [resourceAuthList]);

  // TODO: 某不明情况下, Navigation点击不跳转, 猜测是缓存问题, 手动兼容一下. 后续可下掉
  const handleClickNav = (item: NavigationItem) => {
    console.log("click navigation");
    router.push(item.url);
    if (process.env.NODE_ENV === "production") {
      navigationTimer.current = setTimeout(() => {
        location.href = item.url;
      }, 250);
    }
  };

  useEffect(() => {
    clearTimeout(navigationTimer.current);
  }, [router.asPath]);

  const renderNav = () => {
    return navigationListWithPermission?.map((item) => {
      let isActive = false;

      // Current page is not home; Current navigation is not home
      if (pathname !== "/" && item.url !== "/") {
        if (pathname.trim().startsWith(item.url)) {
          isActive = true;
        }
        if (item.children) {
          item.children.forEach((child) => {
            if (pathname.trim().includes(child)) {
              isActive = true;
            }
          });
        }
        if (!item.url) {
          isActive = false;
        }
      } else {
        isActive = item.url === "/" && pathname === "/";
      }

      // 保证Navigation之间的跳转只刷新Header下面的内容
      return (
        // <Link href={item.url ? item.url : `/`} key={item.url}>
        <div
          key={item.name}
          className={cx(styles.navItemWrap, {
            [styles.navItemActive]: isActive,
          })}
          onClick={() => handleClickNav(item)}
        >
          <div
            className={styles.navItem}
            style={{ fontWeight: isActive ? 600 : 400 }}
          >
            {item.name}
          </div>
        </div>
        // </Link>
      );
    });
  };

  const [visible, setVisible] = useState(false);

  // 客户端中不展示退出
  const notShowLogout = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.navigator?.userAgent?.includes("PddWorkbench") || false;
  }, []);

  const handleLogout = () => {
    logout();
  };

  const renderOverlay = () => {
    return (
      <div className={styles.overlay} id="account_new_overlay">
        <div className={styles.titleRow}>
          {userPicUrl ? (
            <img src={userPicUrl} alt="" className={styles.logoIcon} />
          ) : (
            <span className={styles.logoIcon}>
              <NormalUser className={styles.svgLogoIcon} />
            </span>
          )}
          <div className={styles.title}>
            <div className="flex h-6 w-full items-center">
              <span className="overflow-hidden text-ellipsis whitespace-nowrap text-base font-semibold">
                {name}
              </span>
              {mallOwner && (
                <Tag className="shrink-0" text="主账号" size="small" />
              )}
            </div>
            <div className={styles.subLine}>{`店铺 ID：${mallId}`}</div>
            <div className={styles.subLine}>{`账号名：${username}`}</div>
          </div>
        </div>
        <div className={styles.footerToolsWrapper}>
          {!notShowLogout && (
            <Button
              size="middle"
              type="text"
              className={styles.toolButton}
              onClick={handleLogout}
            >
              退出账号
            </Button>
          )}
        </div>
      </div>
    );
  };

  const toolList = [
    {
      title: "账户",
      icon: <NormalUser />,
      onClick: () =>
        handleClickNav({
          url: "/account_management",
        }),
    },
    {
      title: "消息",
      icon: <NormalMail />,
      onClick: () => setDrawerMessageOpen(true),
    },
    {
      title: "帮助",
      icon: <PromptQuestionCircle className="text-question" />,
      onClick: () => {
        window.open(HELP_CENTER_URL_MAP[userType]);
      },
    },
  ];

  const renderToolItem = (item) => {
    if (item.hidden) {
      return null;
    }

    const itemDom = (
      <div
        className="mr-4 flex cursor-pointer items-center text-sm text-white/[0.85] hover:text-white"
        onClick={item.onClick}
        key={item.title}
      >
        {item.icon}
        <span className="ml-1">{item.title}</span>
      </div>
    );

    if (item.href) {
      return (
        <Link href={item.href} key={item.title}>
          {itemDom}
        </Link>
      );
    }

    return itemDom;
  };

  return (
    <header className={classnames(styles.wrap, "sticky top-0")}>
      <div className={styles.left}>
        <div
          className={classNames(styles.logoWrap, "cursor-pointer")}
          onClick={() => router.push("/")}
        >
          <div className={styles.logoMain}></div>
          <div
            className={styles.logoVersion}
            style={{
              backgroundImage: userTypeTagImageMap[userType]
                ? `url(${userTypeTagImageMap[userType]})`
                : undefined,
            }}
          />
        </div>
        <div className={styles.navWrap}>{renderNav()}</div>
      </div>

      <div className={styles.right}>
        {toolList.map((item) => renderToolItem(item))}
        <Dropdown
          overlay={renderOverlay()}
          visible={visible}
          trigger={["hover"]}
          onVisibleChange={setVisible}
          overlayStyle={{ borderRadius: "8px" }}
        >
          <div className={styles.userInfo}>
            <div className={styles.userLogo}>
              {userPicUrl && <img src={userPicUrl} />}
              {!userPicUrl && <FileImage className={styles.imgIcon} />}
            </div>
            <div className={styles.nameWrapper}>
              <div className="overflow-hidden text-ellipsis whitespace-nowrap text-left">
                {name}
              </div>
              <div className="overflow-hidden text-ellipsis whitespace-nowrap text-left">
                {username}
              </div>
            </div>
            <DirectionDown className={styles.directionIcon} />
          </div>
        </Dropdown>
        <DrawerMessage
          visible={drawerMessageOpen}
          onClose={() => setDrawerMessageOpen(false)}
        />
      </div>
    </header>
  );
};

export { NavHeader };
